import { useEffect, useState } from "react";
import { KeyRound, Shield, Database } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { PageHeader, Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { BackupM365Panel } from "@/pages/admin/BackupM365Panel";
import { QuoteIntroPanel } from "@/pages/admin/QuoteIntroPanel";
import { WebFormPanel } from "@/pages/admin/WebFormPanel";
import { TechFormPanel } from "@/pages/admin/TechFormPanel";
import { BrandingPanel } from "@/pages/admin/BrandingPanel";
import { MailSettingsPanel } from "@/pages/admin/MailSettingsPanel";
import { TenantBackupPanel } from "@/pages/admin/TenantBackupPanel";
import { MicrosoftAccessPanel } from "@/pages/admin/MicrosoftAccessPanel";
import { WorkspacePanel } from "@/pages/admin/WorkspacePanel";

const Row = ({ label, children, testId }) => (
  <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-3 last:border-0" data-testid={testId}>
    <span className="text-sm text-slate-600">{label}</span>{children}
  </div>
);
const OnOff = ({ on, good = true }) => <StatusBadge tone={on ? (good ? "green" : "amber") : (good ? "amber" : "green")}>{on ? "Attivo" : "Disattivo"}</StatusBadge>;

const EntraCard = ({ auth }) => (
  <Panel className="p-5 lg:col-span-2" data-testid="admin-config-env-status-card">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><KeyRound className="h-4 w-4" /></span>
        <div><SectionLabel>Autenticazione</SectionLabel><h2 className="font-display text-lg font-bold text-ink">{auth.provider}</h2></div>
      </div>
      <StatusBadge tone={auth.configured ? "green" : "amber"} data-testid="config-entra-status">{auth.configured ? "Configurato" : "Da configurare"}</StatusBadge>
    </div>
    <p className="mb-3 text-xs text-slate-500">Le variabili sono lette solo dal backend tramite secrets management. I valori non vengono mai mostrati.</p>
    {auth.variables.map((v) => (
      <Row key={v.name} label={<span><span className="font-mono text-xs font-semibold text-slate-800">{v.name}</span>
        <span className="block text-xs text-slate-400">{v.description}</span></span>} testId={`config-var-${v.name}`}>
        <StatusBadge tone={v.configured ? "green" : "amber"}>{v.configured ? "Presente" : "Non definita"}</StatusBadge>
      </Row>
    ))}
    <p className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">Redirect URI da registrare in Entra (piattaforma Web): <span className="font-mono">&lt;APP_BASE_URL&gt;{auth.callback_path}</span></p>
  </Panel>
);

export default function AdminConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  if (!cfg) return <p className="text-sm text-slate-500">Caricamento…</p>;
  const s = cfg.security;
  return (
    <div data-testid="admin-config-page">
      <PageHeader overline="Amministrazione" title="Configurazione" description="Stato della configurazione di sicurezza e dei servizi. Nessun secret viene esposto." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {!cfg.guest && <EntraCard auth={cfg.auth} />}
        {!cfg.guest && <div className="flex flex-col gap-6">
          <Panel className="p-5" data-testid="config-security-card">
            <div className="mb-2 flex items-center gap-2"><Shield className="h-4 w-4 text-slate-400" /><SectionLabel>Sicurezza</SectionLabel></div>
            <Row label="Sessioni server-side"><OnOff on={s.server_side_sessions} /></Row>
            <Row label="Cookie Secure / HttpOnly"><OnOff on={s.cookie_secure} /></Row>
            <Row label="Protezione CSRF"><OnOff on={s.csrf_protection} /></Row>
            <Row label="Rate limiting"><OnOff on={s.rate_limiting} /></Row>
            <Row label="Durata sessione"><span className="text-sm font-medium text-slate-800">{s.session_ttl_hours} ore</span></Row>
            <Row label="Accesso di sviluppo" testId="config-dev-login"><OnOff on={s.dev_login_enabled} good={false} /></Row>
          </Panel>
          <Panel className="p-5" data-testid="config-system-card">
            <div className="mb-2 flex items-center gap-2"><Database className="h-4 w-4 text-slate-400" /><SectionLabel>Sistema</SectionLabel></div>
            <Row label="Database"><StatusBadge tone={cfg.data.database === "ok" ? "green" : "red"}>{cfg.data.database === "ok" ? "Operativo" : "Errore"}</StatusBadge></Row>
            <Row label="Dati dimostrativi"><OnOff on={cfg.data.demo_data_enabled} good={false} /></Row>
            <Row label="Super Admin bootstrap"><OnOff on={cfg.auth.bootstrap_admin_configured} /></Row>
            <Row label="Versione"><span className="font-mono text-xs text-slate-700">{cfg.system.version}</span></Row>
          </Panel>
        </div>}
        {cfg.guest && <MicrosoftAccessPanel />}
        <MailSettingsPanel />
        <WorkspacePanel />
        <BrandingPanel />
        <QuoteIntroPanel />
        <WebFormPanel />
        <TechFormPanel />
        {!cfg.guest && <BackupM365Panel />}
        {cfg.guest && <TenantBackupPanel />}
      </div>
    </div>
  );
}
